import { useEffect, useState } from 'react';
import usePrefersReducedMotion from '@/hooks/usePrefersReducedMotion';
import { CAROUSEL_INTERVAL_MS } from '@/consts/carousel';
import useStyles from './styles';
import type { CarouselItem, MediaCarouselProps } from './types';

/**
 * A multi-output preset's previews, one at a time, advancing on their own.
 *
 * The alternative — subdividing one frame — is what the finished run uses
 * (`RunCard`), where every result is already there to be picked from. A preview
 * is not pickable, so the space is better spent showing each output at full
 * size in turn than showing all of them too small to read.
 *
 * Non-interactive by design: no arrows, no swipe, no position indicator. The
 * hosts are a preset tile and a preview pane that already own the tap (opening
 * the preset) and the horizontal drag (the grid's own scroll), so a control
 * here would compete with both. The rotation is the whole affordance; each
 * slide's `alt` ("Preview 1 of 2") is what carries the count to a screen
 * reader.
 */
const MediaCarousel = ({
  items,
  priority = false,
  fill = false,
  fit = 'cover',
  onSlideClick,
  dataTestId,
}: MediaCarouselProps) => {
  const classes = useStyles();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);

  const total = items.length;

  // Advances for everyone, `prefers-reduced-motion` included — that setting
  // suppresses the *motion* (the slides cross-fade in place instead of
  // travelling, see `trackFade`), not the content. Stopping the rotation strands those
  // users on output 1: with no dots, no badge and no swipe by design, the
  // rotation is the only thing that reveals a preset's other outputs. The
  // cohort is wider than the accessibility setting too — iOS turns
  // reduce-motion on with Low Power Mode, Android with "Remove animations".
  useEffect(() => {
    if (total <= 1) return undefined;

    const timer = window.setInterval(
      () => setIndex((current) => (current + 1) % total),
      CAROUSEL_INTERVAL_MS
    );
    return () => window.clearInterval(timer);
  }, [total]);

  // A preset whose preview count changed under us must not keep pointing past
  // the end — the track would scroll to an empty slot.
  useEffect(() => {
    setIndex((current) => (current < total ? current : 0));
  }, [total]);

  const frameClass = [classes.frame, fill && classes.frameFill]
    .filter(Boolean)
    .join(' ');
  const trackClass = [classes.track, prefersReducedMotion && classes.trackFade]
    .filter(Boolean)
    .join(' ');

  const slideClass = (slide: number) =>
    [
      classes.slide,
      prefersReducedMotion && classes.slideFade,
      prefersReducedMotion && slide === index && classes.slideFadeVisible,
    ]
      .filter(Boolean)
      .join(' ');

  // Only the slide on screen gets a live element. A card with four video
  // outputs would otherwise hold four decoders open, and a grid of those cards
  // multiplies it by the page — this keeps it at one per carousel, which is
  // what makes a playing tile affordable at all.
  const mediaClass = [classes.media, fit === 'contain' && classes.mediaContain]
    .filter(Boolean)
    .join(' ');

  const renderSlide = (item: CarouselItem, slide: number) =>
    item.videoSrc && slide === index ? (
      <video
        className={mediaClass}
        poster={item.src}
        src={item.videoSrc}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        controls={false}
        disableRemotePlayback
        disablePictureInPicture
        aria-label={item.alt}
      />
    ) : (
      <img
        className={mediaClass}
        src={item.src}
        alt={item.alt ?? ''}
        loading={priority && slide === 0 ? 'eager' : 'lazy'}
        draggable={false}
      />
    );

  // A button only when it acts — a preset preview does nothing on tap, and a
  // button that ignores clicks lies to a screen reader.
  const Frame = onSlideClick ? 'button' : 'div';

  return (
    <Frame
      className={[frameClass, onSlideClick && classes.interactive]
        .filter(Boolean)
        .join(' ')}
      data-testid={dataTestId}
      {...(onSlideClick
        ? {
            type: 'button' as const,
            onClick: () => onSlideClick(index),
            'aria-label': items[index]?.alt,
          }
        : {})}
    >
      {/* No transform in fade mode — the slides are stacked, so translating the
          track would carry the whole stack off screen. */}
      <div
        className={trackClass}
        style={
          prefersReducedMotion
            ? undefined
            : { transform: `translateX(-${index * 100}%)` }
        }
      >
        {items.map((item, slide) => (
          <div key={item.key} className={slideClass(slide)}>
            {renderSlide(item, slide)}
          </div>
        ))}
      </div>
    </Frame>
  );
};

export default MediaCarousel;
